import PublicBaseEventRepository from "@app-contexts/public/features/event-detail/data/repositories/PublicBaseEventRepository";
import {
  CheckEventPasswordResponseType,
  TicketTypeSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";

/**
 * For items without passwords, returns false
 * For items with passwords, returns true if the item is not in the authorized array
 * @param {object} inputItem
 * @param {object} itemAuthArray
 * @param {function} updateItemAuthArray
 * @return {boolean}
 */
export const showItemPasswordInput = (
  inputItem: TicketTypeSerializer | SeatingItemPropertyMap,
  itemAuthArray: Array<{ id: number; authorized: boolean }>,
  updateItemAuthArray: (
    input: Array<{ id: number; authorized: boolean }>
  ) => void
): boolean => {
  if (
    inputItem.is_password_protected &&
    !itemAuthArray.find((ticketAuth) => inputItem.id === ticketAuth.id)
  ) {
    updateItemAuthArray([
      ...itemAuthArray,
      {
        id: inputItem.id,
        authorized: false,
      },
    ]);

    // Explicitly return true here, since inputItem is password_protected AND is now part of the itemAuthArray
    return true;
  }

  return (
    inputItem.is_password_protected &&
    Boolean(
      itemAuthArray?.find(
        (ticketAuth) => ticketAuth.id === inputItem.id && !ticketAuth.authorized
      )
    )
  );
};

type AuthorizeableEvent<T = unknown> = T & {
  is_password_protected?: boolean;
  slug?: string;
};

/**
 * @deprecated See GlobalAuthorizationProvider
 * Checks if event requires password and user is authorized to view it.
 * Also will return the allowed ticket types given the inputted password.
 */
export const loadAuthorizedToViewEvent = (
  inputEvent: AuthorizeableEvent
): Promise<{
  isAuthorized: boolean;
  ticketTypesAuthorized?: Array<number> | null;
}> => {
  const repo = new PublicBaseEventRepository();

  return new Promise((resolve) => {
    // Only check authorization if the event is password-protected
    // For events without password protection, show the event page info right away
    if (inputEvent?.is_password_protected) {
      repo
        .checkEventPasswordAuthorized(inputEvent.slug, {
          showToastOnError: false,
        })
        .then((res: CheckEventPasswordResponseType) => {
          // user is authorized for the event, hide the password modal
          // set ticket types to display based on the password
          let ticketTypesAuthorized = null;
          if (res) {
            ticketTypesAuthorized = res.tickettypes.map((tt) => Number(tt));
          }
          resolve({ isAuthorized: true, ticketTypesAuthorized });
        })
        .catch(() => {
          // user is not authorized for the event, show the password modal
          resolve({ isAuthorized: false });
        });
    }
    // if the event is not password-protected,
    // return all tickets (represented by empty array)
    if (!inputEvent?.is_password_protected) {
      resolve({ isAuthorized: true, ticketTypesAuthorized: [] });
    }
  });
};

// regex expression used to check that a password has at least 1 special character
export const passwordRegex = /[@#$-/:-?{-~!^_`\[\]()*\\\\]/; // eslint-disable-line
// regex expression used to check the number of special characters in a password. This is used to calculate password strength
export const passwordStrengthRegex = /[@#$-/:-?{-~!^_`\[\]()*\\\\]/g; // eslint-disable-line
